/**
 * Reusable Figaro document extracts / citation viewer.
 *
 * Split pane: clause list (A) + live document text with highlight (B).
 * Primary UX is in-app; analysis / file download are secondary links.
 *
 * Props API — pass to mountDocExtractsViewer(host, opts):
 *
 *   extracts: Array<{
 *     id: string|number,
 *     text: string,                 // clause / passage body (list + fallback highlight)
 *     label?: string,               // short title
 *     documentId: number|string,    // required for doc pane
 *     documentName?: string,
 *     documentKind?: string,        // LEASE, MPD, …
 *     documentDate?: string,
 *     clauseRef?: string,           // § / clause / section label
 *     page?: number,                // 1-based page
 *     chunkId?: number|string,
 *     startOffset?: number,         // within page text (optional)
 *     endOffset?: number,
 *     quote?: string,               // preferred highlight needle
 *   }>
 *   documents?: Array<{ id|documentId, title?, originalFilename?, docType?, … }>
 *   contextTitle?: string
 *   selectedId?: string|number
 *   onSelect?: (extract) => void
 *   showOriginal?: boolean          // PDF iframe (default true)
 *   compact?: boolean
 *   emptyMessage?: string
 *   el, fetchJson, apiUrl, cleanText?  — app-v2 helpers
 *
 * Highlight order: offsets → quote → extract.text needle → clauseRef →
 * in-doc search (package/code) → scroll to page/chunk.
 *
 * Phase-2: true PDF.js pixel highlight (today: HTML/text chunks + PDF iframe #page=).
 */

const STYLE_ID = "figaro-doc-extracts-styles";

function ensureStyles() {
  if (typeof document === "undefined" || document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
.fde-root { display:flex; flex-direction:column; gap:.75rem; }
.fde-grid { display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1.15fr); gap:.75rem; }
@media (max-width: 960px) { .fde-grid { grid-template-columns:1fr; } }
.fde-panel { background:#fff; border:1px solid #e5e7eb; border-radius:.75rem; box-shadow:0 1px 2px rgba(16,24,40,.04); overflow:hidden; display:flex; flex-direction:column; min-height:0; }
.fde-panel-head { display:flex; align-items:center; justify-content:space-between; gap:.5rem; padding:.55rem .85rem; border-bottom:1px solid #f3f4f6; background:#fcfcfd; }
.fde-panel-title { font-size:.66rem; letter-spacing:.06em; text-transform:uppercase; color:#9ca3af; font-weight:600; }
.fde-list { overflow-y:auto; max-height:28rem; }
.fde-item { width:100%; text-align:left; border:0; border-bottom:1px solid #f3f4f6; background:#fff; padding:.7rem .85rem; cursor:pointer; display:block; }
.fde-item:hover { background:#f9fafb; }
.fde-item.is-on { background:#fef2f2; box-shadow:inset 3px 0 0 #b91c1c; }
.fde-item-label { font-size:.8125rem; font-weight:600; color:#111827; margin:0 0 .25rem; }
.fde-item-text { font-size:.75rem; color:#374151; line-height:1.45; white-space:pre-wrap; display:-webkit-box; -webkit-line-clamp:4; -webkit-box-orient:vertical; overflow:hidden; }
.fde-item-cite { margin-top:.35rem; font-size:.68rem; color:#6b7280; line-height:1.35; }
.fde-doc-meta { padding:.55rem .85rem; border-bottom:1px solid #f3f4f6; display:flex; flex-wrap:wrap; gap:.4rem .75rem; align-items:center; font-size:.75rem; color:#4b5563; }
.fde-link { color:#b91c1c; text-decoration:none; font-weight:600; }
.fde-link:hover { text-decoration:underline; }
.fde-sec-btn { display:inline-flex; align-items:center; gap:.25rem; font-size:.72rem; font-weight:600; color:#374151; background:#fff; border:1px solid #e5e7eb; border-radius:.4rem; padding:.2rem .55rem; cursor:pointer; text-decoration:none; }
.fde-sec-btn:hover { background:#f9fafb; border-color:#d1d5db; }
.fde-toolbar { display:flex; flex-wrap:wrap; gap:.4rem; align-items:center; padding:.45rem .85rem; border-bottom:1px solid #f3f4f6; }
.fde-select { border:1px solid #e5e7eb; border-radius:.5rem; padding:.25rem .5rem; font-size:.75rem; background:#fff; }
.fde-badge { font-size:.68rem; color:#92400e; background:#fffbeb; border:1px solid #fde68a; border-radius:.35rem; padding:.1rem .45rem; }
.fde-text { font-size:.8125rem; white-space:pre-wrap; font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace; line-height:1.55; color:#111827; max-height:22rem; overflow-y:auto; padding:.85rem; background:#f9fafb; }
.fde-text mark { background:#fef08a; padding:0 .15rem; border-radius:.15rem; }
.fde-text mark.fde-flash { outline:2px solid #b91c1c; outline-offset:1px; }
.fde-iframe { width:100%; height:18rem; border:0; border-top:1px solid #e5e7eb; background:#fff; }
.fde-empty { padding:1rem .85rem; font-size:.8125rem; color:#6b7280; }
.fde-compact .fde-list { max-height:16rem; }
.fde-compact .fde-text { max-height:14rem; }
.fde-compact .fde-iframe { height:12rem; }
`;
  document.head.appendChild(style);
}

function escapeHtml(s) {
  return String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Highlight all case-insensitive matches of `query` in plain text → HTML. */
export function highlightExtractHtml(text, query) {
  const esc = escapeHtml(text);
  const q = String(query || "").trim();
  if (!q || q.length < 2) return esc;
  const safe = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  try {
    return esc.replace(new RegExp(`(${safe})`, "gi"), '<mark class="fde-hl">$1</mark>');
  } catch {
    return esc;
  }
}

function fmtDateShort(v) {
  if (!v) return "";
  try {
    return new Date(v).toLocaleDateString();
  } catch {
    return String(v);
  }
}

function docAnalysisHref(docId, { page, q } = {}) {
  const base = `${window.location.pathname}${window.location.search}`;
  const params = new URLSearchParams();
  if (page) params.set("page", String(page));
  if (q) params.set("q", String(q).slice(0, 120));
  const qs = params.toString();
  return `${base}#/figaro/documents/${docId}${qs ? `?${qs}` : ""}`;
}

function normalizeDocId(d) {
  return Number(d?.documentId ?? d?.id);
}

/**
 * Normalize a loose extract / citation into the stable props shape.
 */
export function normalizeExtract(raw, index = 0) {
  if (!raw) return null;
  const documentId = raw.documentId ?? raw.docId ?? raw.document?.documentId ?? raw.document?.id;
  const text =
    raw.text ||
    raw.quote ||
    raw.excerpt ||
    raw.content ||
    raw.note ||
    "";
  const page = raw.page ?? raw.pageStart ?? raw.page_number ?? null;
  return {
    id: raw.id ?? raw.ref ?? `extract-${index}`,
    text: String(text),
    label: raw.label || raw.title || raw.ref || null,
    documentId: documentId != null && documentId !== "" ? Number(documentId) : null,
    documentName:
      raw.documentName ||
      raw.title ||
      raw.originalFilename ||
      raw.document?.title ||
      raw.document?.originalFilename ||
      null,
    documentKind: raw.documentKind || raw.docType || raw.kind || raw.document?.docType || null,
    documentDate: raw.documentDate || raw.date || raw.document?.date || raw.document?.documentDate || null,
    clauseRef: raw.clauseRef || raw.clause || raw.sectionLabel || raw.section || null,
    page: page != null && Number(page) > 0 ? Number(page) : null,
    chunkId: raw.chunkId ?? raw.chunk_id ?? null,
    startOffset: raw.startOffset ?? raw.start ?? null,
    endOffset: raw.endOffset ?? raw.end ?? null,
    quote: raw.quote || null,
    searchHint: raw.searchHint || raw.code || null,
    _raw: raw,
  };
}

/** Map Figaro chat message citations → extracts (document kind only). */
export function extractsFromChatCitations(citations = []) {
  return (citations || [])
    .filter((c) => c && c.kind !== "web" && c.kind !== "workbook" && c.documentId)
    .map((c, i) =>
      normalizeExtract(
        {
          id: c.ref || `S${i + 1}`,
          label: c.ref ? `${c.ref} · ${c.title || "Document"}` : c.title || `Citation ${i + 1}`,
          text:
            c.excerpt ||
            c.content ||
            c.quote ||
            (c.pageStart
              ? `Cited passage on page ${c.pageStart}${c.pageEnd && c.pageEnd !== c.pageStart ? `–${c.pageEnd}` : ""}.`
              : "Open the document pane to inspect the cited source."),
          documentId: c.documentId,
          documentName: c.title || c.filename,
          documentKind: c.docType || "DOCUMENT",
          page: c.pageStart,
          chunkId: c.chunkId,
          clauseRef: c.sheetName || c.sectionLabel || null,
          quote: c.quote || c.excerpt || null,
        },
        i
      )
    )
    .filter(Boolean);
}

/**
 * Map workscope lease MR conditions → extracts (governing lease / amendments).
 */
export function extractsFromLeaseConditions(conditions = [], governingDocument = null) {
  return (conditions || [])
    .map((c, i) => {
      const cite = c.citation || governingDocument || null;
      const documentId = cite?.documentId || governingDocument?.documentId;
      if (!documentId) return null;
      const label = [c.code, c.label].filter(Boolean).join(" — ") || `Condition ${i + 1}`;
      const text =
        cite?.quote ||
        cite?.excerpt ||
        cite?.note ||
        c.criteria ||
        `MR package ${c.code || label}: confirm reimbursable wording in the linked lease / amendment.`;
      return normalizeExtract(
        {
          id: c.id || c.code || `cond-${i}`,
          label,
          text,
          documentId,
          documentName: cite?.title || governingDocument?.title,
          documentKind: cite?.docType || cite?.role || governingDocument?.docType || "LEASE",
          documentDate: cite?.date || governingDocument?.date,
          clauseRef: cite?.clause || c.conditionRef || null,
          page: cite?.pageStart || cite?.page || null,
          chunkId: cite?.chunkId || null,
          quote: cite?.quote || null,
          searchHint: c.code || null,
        },
        i
      );
    })
    .filter(Boolean);
}

/** Map draft-row lease citations (table A) → unique extracts. */
export function extractsFromDraftLeaseRows(rows = []) {
  const seen = new Set();
  const out = [];
  for (const r of rows || []) {
    const cite = r.leaseCitation;
    if (!cite?.documentId) continue;
    const key = `${cite.documentId}:${r.leaseConditionRef || r.workscopeTask || ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(
      normalizeExtract({
        id: key,
        label: r.leaseConditionRef || r.workscopeTask || cite.title,
        text:
          r.notEligibleReason ||
          r.leaseCriteria ||
          cite.note ||
          `Lease reference for task ${r.workscopeTask || "—"}.`,
        documentId: cite.documentId,
        documentName: cite.title,
        documentKind: cite.docType || "LEASE",
        documentDate: cite.date,
        clauseRef: r.leaseConditionRef || cite.note,
        quote: cite.quote || null,
      })
    );
  }
  return out;
}

/**
 * Map lease-return report rows / section blocks that carry a `source` object
 * ({ documentId, title, clause, page, excerpt }) — stable for return-conditions UI.
 */
export function extractsFromLeaseReturnSources(rows = []) {
  const seen = new Set();
  const out = [];
  for (let i = 0; i < (rows || []).length; i++) {
    const r = rows[i];
    const src = r?.source || r;
    if (!src?.documentId) continue;
    const id = r.key || r.id || `${src.documentId}:${src.clause || src.page || i}`;
    if (seen.has(String(id))) continue;
    seen.add(String(id));
    out.push(
      normalizeExtract(
        {
          id,
          label: r.label || r.key || src.clause || src.title || `Condition ${i + 1}`,
          text: src.excerpt || src.quote || r.summary || r.detail || "",
          documentId: src.documentId,
          documentName: src.title,
          documentKind: src.docType || "LEASE",
          clauseRef: src.clause,
          page: src.page ?? src.pageStart,
          chunkId: src.chunkId,
          quote: src.quote || src.excerpt || null,
        },
        i
      )
    );
  }
  return out.filter(Boolean);
}

function highlightNeedle(extract) {
  const quote = String(extract?.quote || "").trim();
  if (quote.length >= 3) return quote.slice(0, 160);
  const text = String(extract?.text || "").trim();
  // Prefer a mid-length contiguous phrase from clause text (skip boilerplate notes).
  if (text.length >= 12 && !/^MR package|^Confirm |^Cited passage|^Open the document/i.test(text)) {
    const line = text.split(/\n/).map((l) => l.trim()).find((l) => l.length >= 12) || text;
    return line.slice(0, 100);
  }
  const clause = String(extract?.clauseRef || "").trim();
  if (clause.length >= 2) return clause;
  const hint = String(extract?.searchHint || extract?.label || "").trim();
  // Prefer short codes like 9YR / 2A for in-doc search
  const code = hint.match(/\b\d{1,3}(?:YR|YE|A|C)\b/i)?.[0];
  return code || "";
}

function applyOffsetHighlight(pageText, start, end) {
  const s = Math.max(0, Number(start) || 0);
  const e = Math.min(String(pageText || "").length, Number(end) || 0);
  if (!(e > s)) return null;
  const before = escapeHtml(pageText.slice(0, s));
  const mid = escapeHtml(pageText.slice(s, e));
  const after = escapeHtml(pageText.slice(e));
  return `${before}<mark class="fde-hl fde-flash">${mid}</mark>${after}`;
}

/**
 * Mount (or remount) the extracts viewer into `host`.
 * Returns { select(id), getSelected(), refresh(nextExtracts), destroy() }.
 */
export function mountDocExtractsViewer(host, opts = {}) {
  ensureStyles();
  const {
    el,
    fetchJson,
    apiUrl,
    cleanText = (s) => s,
    contextTitle = "Relevant extracts",
    documents = [],
    showOriginal = true,
    compact = false,
    emptyMessage = "No extracts for this context yet.",
    onSelect = null,
  } = opts;

  let extracts = (opts.extracts || []).map((x, i) => normalizeExtract(x, i)).filter(Boolean);
  let selectedId = opts.selectedId != null ? opts.selectedId : extracts[0]?.id ?? null;
  const docCache = new Map();
  const pageCache = new Map();

  const root = el("div", { class: `fde-root${compact ? " fde-compact" : ""}` });
  const listHost = el("div", { class: "fde-list" });
  const docMeta = el("div", { class: "fde-doc-meta" });
  const toolbar = el("div", { class: "fde-toolbar" });
  const textHost = el("div", { class: "fde-text", text: "Select an extract to open the source." });
  const iframeHost = el("div", {});

  const countEl = el("span", { class: "text-xs text-gray-400 tabular-nums", text: "" });
  const listPanel = el("div", { class: "fde-panel" }, [
    el("div", { class: "fde-panel-head" }, [
      el("span", { class: "fde-panel-title", text: contextTitle }),
      countEl,
    ]),
    listHost,
  ]);

  const docPanel = el("div", { class: "fde-panel" }, [
    el("div", { class: "fde-panel-head" }, [
      el("span", { class: "fde-panel-title", text: "Source document" }),
    ]),
    docMeta,
    toolbar,
    textHost,
    iframeHost,
  ]);

  root.appendChild(el("div", { class: "fde-grid" }, [listPanel, docPanel]));
  host.replaceChildren(root);

  function docMetaFor(extract) {
    const id = extract?.documentId;
    const fromList = (documents || []).find((d) => normalizeDocId(d) === Number(id));
    return {
      id,
      name:
        extract?.documentName ||
        fromList?.title ||
        fromList?.originalFilename ||
        (id ? `Document ${id}` : "—"),
      kind: extract?.documentKind || fromList?.docType || fromList?.role || null,
      date: extract?.documentDate || fromList?.documentDate || fromList?.createdAt || null,
    };
  }

  async function loadDocument(docId) {
    const id = Number(docId);
    if (!Number.isFinite(id)) return null;
    if (docCache.has(id)) return docCache.get(id);
    const data = await fetchJson(apiUrl(`/api/ai/documents/${id}`));
    docCache.set(id, data);
    return data;
  }

  async function loadPage(docId, page) {
    const id = Number(docId);
    const n = Number(page) || 1;
    const key = `${id}:${n}`;
    if (pageCache.has(key)) return pageCache.get(key);
    const data = await fetchJson(apiUrl(`/api/ai/documents/${id}/pages/${n}`));
    const text = (data.pages || []).map((p) => p.text || "").join("\n\n---\n\n");
    const row = { text, pages: data.pages || [] };
    pageCache.set(key, row);
    return row;
  }

  async function resolvePageAndNeedle(extract, docData) {
    let page = extract.page || null;
    let needle = highlightNeedle(extract);
    const chunks = docData?.chunks || [];

    if (extract.chunkId) {
      const hit = chunks.find((c) => Number(c.id) === Number(extract.chunkId));
      if (hit) {
        page = page || hit.pageStart || 1;
        if (!needle && hit.excerpt) needle = String(hit.excerpt).slice(0, 80);
      }
    }

    if (!page && needle && chunks.length) {
      const lower = needle.toLowerCase();
      const hit = chunks.find((c) => String(c.excerpt || "").toLowerCase().includes(lower.slice(0, 40)));
      if (hit) page = hit.pageStart || 1;
    }

    // In-doc search fallback via FTS when we have a short code / needle but no page
    if ((!page || !String(extract.text || "").trim()) && needle && extract.documentId) {
      try {
        const q = encodeURIComponent(needle.slice(0, 80));
        const res = await fetchJson(
          apiUrl(`/api/ai/search?documentId=${extract.documentId}&q=${q}&limit=5`)
        );
        const hit = (res.hits || [])[0];
        if (hit) {
          page = page || hit.pageStart || 1;
          if (!extract.quote && hit.excerpt) {
            needle = String(hit.excerpt).slice(0, 100);
          }
          if ((!extract.text || extract.text.length < 40) && hit.excerpt) {
            extract.text = hit.excerpt;
          }
          if (!extract.chunkId && hit.chunkId) extract.chunkId = hit.chunkId;
        }
      } catch {
        /* search optional */
      }
    }

    return { page: page || 1, needle };
  }

  function paintList() {
    countEl.textContent = extracts.length ? `${extracts.length}` : "";
    if (!extracts.length) {
      listHost.replaceChildren(el("div", { class: "fde-empty", text: emptyMessage }));
      return;
    }
    listHost.replaceChildren(
      ...extracts.map((ex) => {
        const meta = docMetaFor(ex);
        const citeParts = [
          meta.kind,
          meta.name,
          fmtDateShort(meta.date),
          ex.clauseRef ? `§ ${ex.clauseRef}` : null,
          ex.page ? `p.${ex.page}` : null,
        ].filter(Boolean);
        const btn = el("button", {
          type: "button",
          class: `fde-item${String(ex.id) === String(selectedId) ? " is-on" : ""}`,
          onClick: () => void select(ex.id),
        }, [
          el("div", { class: "fde-item-label", text: ex.label || meta.name || "Extract" }),
          el("div", { class: "fde-item-text", text: ex.text || "(no clause text)" }),
          el("div", { class: "fde-item-cite", text: citeParts.join(" · ") }),
        ]);
        return btn;
      })
    );
  }

  async function paintDoc(extract) {
    if (!extract?.documentId) {
      docMeta.replaceChildren(el("span", { text: "No document linked to this extract." }));
      toolbar.replaceChildren();
      textHost.textContent = "";
      iframeHost.replaceChildren();
      return;
    }

    const meta = docMetaFor(extract);
    docMeta.replaceChildren(
      ...[
        el("span", {
          class: "font-semibold text-gray-800",
          text: [meta.kind, meta.name].filter(Boolean).join(" · "),
        }),
        meta.date ? el("span", { text: fmtDateShort(meta.date) }) : null,
        extract.clauseRef ? el("span", { text: `Clause ${extract.clauseRef}` }) : null,
      ].filter(Boolean)
    );

    textHost.textContent = "Loading…";
    toolbar.replaceChildren();
    iframeHost.replaceChildren();

    try {
      const docData = await loadDocument(extract.documentId);
      const doc = docData?.document || {};
      const { page, needle } = await resolvePageAndNeedle(extract, docData);

      const pageSel = el("select", { class: "fde-select" });
      const pageNums = (docData.pages || []).map((p) => p.pageNumber);
      const nums = pageNums.length ? pageNums : [page];
      for (const n of nums) {
        pageSel.appendChild(
          el("option", {
            value: String(n),
            text: `Page ${n}`,
            selected: Number(n) === Number(page) ? "selected" : undefined,
          })
        );
      }
      pageSel.value = String(page);

      const hlBadge = needle
        ? el("span", { class: "fde-badge", text: `Highlight: ${needle.slice(0, 48)}${needle.length > 48 ? "…" : ""}` })
        : null;

      const analysisHref = docAnalysisHref(extract.documentId, { page, q: needle });
      toolbar.replaceChildren(
        ...[
          el("span", { class: "text-xs text-gray-500", text: "Page" }),
          pageSel,
          hlBadge,
          el("a", {
            href: analysisHref,
            target: "_blank",
            rel: "noopener",
            class: "fde-sec-btn",
            title: "Open full analysis in new tab",
            text: "Analysis ↗",
          }),
          el("a", {
            href: apiUrl(`/api/ai/documents/${extract.documentId}/file`),
            target: "_blank",
            rel: "noopener",
            class: "fde-sec-btn",
            title: "Download / open original file",
            text: "File ⇩",
          }),
        ].filter(Boolean)
      );

      const renderPage = async (n) => {
        textHost.textContent = "Loading…";
        try {
          const pg = await loadPage(extract.documentId, n);
          const pageText = pg.text || "(empty)";
          let html = null;
          if (
            extract.startOffset != null &&
            extract.endOffset != null &&
            Number(extract.endOffset) > Number(extract.startOffset)
          ) {
            html = applyOffsetHighlight(pageText, extract.startOffset, extract.endOffset);
          }
          if (!html) html = highlightExtractHtml(pageText, needle);
          textHost.innerHTML = html;
          const mark = textHost.querySelector("mark");
          if (mark) {
            mark.classList.add("fde-flash");
            mark.scrollIntoView({ block: "center", behavior: "smooth" });
          }
        } catch (e) {
          textHost.textContent = cleanText(e?.message) || String(e);
        }

        const isPdf =
          /\.pdf$/i.test(doc.originalFilename || "") || String(doc.mimeType || "").includes("pdf");
        if (showOriginal && isPdf) {
          const src = `${apiUrl(`/api/ai/documents/${extract.documentId}/file`)}#page=${n}`;
          iframeHost.replaceChildren(
            el("iframe", {
              class: "fde-iframe",
              title: "Original PDF",
              src,
            })
          );
        } else if (showOriginal) {
          iframeHost.replaceChildren(
            el("div", { class: "px-3 py-2 text-xs text-gray-500 border-t border-gray-100" }, [
              el("a", {
                href: apiUrl(`/api/ai/documents/${extract.documentId}/file`),
                target: "_blank",
                rel: "noopener",
                class: "fde-link",
                text: "Open original file",
              }),
            ])
          );
        } else {
          iframeHost.replaceChildren();
        }
      };

      pageSel.onchange = () => void renderPage(Number(pageSel.value) || 1);
      await renderPage(page);
    } catch (e) {
      textHost.textContent = cleanText(e?.message) || String(e);
    }
  }

  async function select(id) {
    selectedId = id;
    const extract = extracts.find((x) => String(x.id) === String(id)) || null;
    paintList();
    await paintDoc(extract);
    if (typeof onSelect === "function" && extract) onSelect(extract);
  }

  function refresh(nextExtracts, nextSelectedId) {
    extracts = (nextExtracts || []).map((x, i) => normalizeExtract(x, i)).filter(Boolean);
    selectedId =
      nextSelectedId != null
        ? nextSelectedId
        : extracts.some((x) => String(x.id) === String(selectedId))
          ? selectedId
          : extracts[0]?.id ?? null;
    paintList();
    const extract = extracts.find((x) => String(x.id) === String(selectedId)) || null;
    void paintDoc(extract);
  }

  paintList();
  const initial = extracts.find((x) => String(x.id) === String(selectedId)) || extracts[0] || null;
  if (initial) void paintDoc(initial);
  else {
    docMeta.replaceChildren();
    toolbar.replaceChildren();
    textHost.textContent = emptyMessage;
  }

  return {
    select: (id) => void select(id),
    getSelected: () => extracts.find((x) => String(x.id) === String(selectedId)) || null,
    refresh,
    destroy: () => {
      host.replaceChildren();
    },
  };
}

/**
 * Convenience: build a detached root node (same as mount into a temp host).
 */
export function renderDocExtractsViewer(opts) {
  const host = opts.el("div", {});
  const api = mountDocExtractsViewer(host, opts);
  host._fde = api;
  return host;
}
